import api from './axiosConfig';

export interface Occupant {
    name: string;
    surname: string;
    personal_id: string;
    employer_name?: string | null;
    monthly_income?: string | null;
    cell_number?: string | null;
}

export interface IndigentFormData {
    ward_no: string;
    electricity_meter_no: string;
    cell_number?: string;
    email?: string;
    erf_no: string;
    service_electricity: boolean;
    service_waste_water: boolean;
    name: string;
    sa_id: string;
    source_of_income?: string;
    service_water: boolean;
    legacy_application_number?: string;
    employer_name?: string;
    service_waste: boolean;
    occupants: Occupant[]; // Array of Occupant objects
    address_line_1?: string;
    address_line_2?: string;
    town: string;
    address_line_3?: string;
    financial_year: string;
    document_types: string; // Comma-separated string
    surname: string;
    account_number: string;
    monthly_income?: string;
    declaration_accepted: boolean;
    signature_file: File;
    occupant_photos: File[];
    files: File[];
    indigent_number?: string; // Add indigent_number for edit mode
}

// Interface for API response when fetching indigent details
export interface IndigentDetailsResponse {
    id: string;
    request_id: string;
    ward_no: string;
    electricity_meter_no: string;
    cell_number?: string;
    email?: string;
    erf_no: string;
    service_electricity: boolean;
    service_waste_water: boolean;
    name: string;
    sa_id: string;
    source_of_income?: string;
    service_water: boolean;
    legacy_application_number?: string;
    employer_name?: string;
    service_waste: boolean;
    occupants: Occupant[];
    address_line_1?: string;
    address_line_2?: string;
    town: string;
    address_line_3?: string;
    financial_year: string;
    document_types: string;
    surname: string;
    account_number: string;
    monthly_income?: string;
    declaration_accepted?: boolean;
    attachments: Array<{
        id: string;
        file_path: string;
        file_name: string;
        document_type: string;
    }>;
    signature_file?: string;
    created_at: string;
    updated_at: string;
    status: string;
}

interface IndigentResponse {
    // Response interface fields...
}

export const indigentService = {
    createIndigent: async (data: IndigentFormData): Promise<IndigentResponse> => {
        try {
            const formData = new FormData();

            // Add all string/boolean fields first
            Object.entries(data).forEach(([key, value]) => {
                if (
                    key !== 'files' &&
                    key !== 'occupant_photos' &&
                    key !== 'signature_file' &&
                    key !== 'occupants'
                ) {
                    const stringValue =
                        value === undefined || value === null ? '' : value.toString();
                    formData.append(key, stringValue);
                }
            });

            // Add occupants as JSON string
            formData.append('occupants_json', JSON.stringify(data.occupants || []));

            // Add signature file with explicit name and type
            if (data.signature_file instanceof File) {
                formData.append('signature_file', data.signature_file, data.signature_file.name);
            }

            // Add supporting documents with explicit names and types
            if (Array.isArray(data.files)) {
                data.files.forEach(file => {
                    if (file instanceof File) {
                        formData.append(`files`, file, file.name);
                    }
                });
            }

            // Add occupant photos with explicit names and types
            if (Array.isArray(data.occupant_photos)) {
                data.occupant_photos.forEach(photo => {
                    if (photo instanceof File) {
                        formData.append('occupant_photos', photo, photo.name);
                    }
                });
            }
            // Debug: Log FormData contents
            //   console.log('=== FormData Contents ===');
            //   formData.forEach((value, key) => {
            //     if (value instanceof File) {
            //       console.log(`${key}:`, `File(${value.name}, ${value.size} bytes, ${value.type})`);
            //     } else {
            //       console.log(`${key}:`, value);
            //     }
            //   });
            //   console.log('=== End FormData ===');

            const response = await api.post<IndigentResponse>('/add-indigent/submit', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Get indigent details by request ID
    getIndigentById: async (requestId: string): Promise<IndigentDetailsResponse> => {
        try {
            const response = await api.get<IndigentDetailsResponse>(
                `/indigent-search/view/${requestId}`,
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Update indigent by request ID
    updateIndigent: async (
        requestId: string,
        data: IndigentFormData,
    ): Promise<IndigentResponse> => {
        try {
            const formData = new FormData();

            // Add all string/boolean fields first
            Object.entries(data).forEach(([key, value]) => {
                if (
                    key !== 'files' &&
                    key !== 'occupant_photos' &&
                    key !== 'signature_file' &&
                    key !== 'occupants'
                ) {
                    const stringValue =
                        value === undefined || value === null ? '' : value.toString();
                    formData.append(key, stringValue);
                }
            });

            // Add occupants as JSON string
            formData.append('occupants_json', JSON.stringify(data.occupants || []));

            // Add signature file with explicit name and type
            if (data.signature_file instanceof File) {
                formData.append('signature_file', data.signature_file, data.signature_file.name);
            }

            // Add supporting documents with explicit names and types
            if (Array.isArray(data.files)) {
                data.files.forEach(file => {
                    if (file instanceof File) {
                        formData.append(`files`, file, file.name);
                    }
                });
            }

            // Add occupant photos with explicit names and types
            if (Array.isArray(data.occupant_photos)) {
                data.occupant_photos.forEach(photo => {
                    if (photo instanceof File) {
                        formData.append('occupant_photos', photo, photo.name);
                    }
                });
            }

            const response = await api.post<IndigentResponse>(
                '/add-indigent/submit',
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                },
            );

            return response.data;
        } catch (error) {
            throw error;
        }
    },
};
